/**
 * 이상한 방 - 카탈로그 엔트리 작성 계약
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 4.1절(그리드 해상도) · 4.2절(무변화 회전) · 8절(카탈로그 규격 요건)
 * 에셋 매니페스트: `public/assets/models.json` · 시각 색인: `public/assets/catalog-preview.png`
 *
 * ## 이 파일이 존재하는 이유
 *
 * `public/assets/models/` 에 Kenney CC0 GLB **196종**이 있는데 `catalog.ts` 는 44종만 배선돼 있었다.
 * 그 결과 391개 배치에서 같은 물건이 40번씩 반복되고, 술래는 형태가 아니라 **개수 세기**로
 * 문제를 푼다(컨셉시트 4.2가 무너진다). 남은 152종을 배선하는 작업은 여러 명이 나눠 쓰는데,
 * 각자 다른 규칙으로 적으면 카탈로그는 늘어나도 **일관성이 사라져** 오히려 판정이 흔들린다.
 *
 * 그래서 이 파일은 **규격만** 담는다 - 실제 엔트리 목록은 각 그룹 파일이, 최종 병합은 `catalog.ts` 가 한다.
 * 여기 있는 타입/헬퍼는 전부 "손으로 추정하면 반드시 어긋나는 것"을 기계에 넘기기 위한 장치다.
 *
 * ## 작성자가 지켜야 할 것 (요약)
 *
 * 1. `footprint` 는 **반드시 `cellsFromMeters()` 로 계산**한다. 눈대중 금지.
 *    매니페스트의 `sizeMeters` 는 스케일이 이미 반영된 실측값이라 손으로 다시 곱할 이유가 없다.
 * 2. `rotSymmetry` 는 `catalog-preview.png` 를 **눈으로 보고** 판정한다.
 *    선언과 형상이 어긋나면 "서버 diff에는 잡히는데 화면에는 아무 일도 없는" 변화가 생긴다.
 *    이건 어려운 난이도가 아니라 **찾는 것이 물리적으로 불가능**한 것이고, 원칙 2 위반이다.
 * 3. 실루엣이 겹치는 변종은 **한쪽만** 넣는다. 비슷한 의자 5종은 난이도가 아니라 짜증이다.
 * 4. 기존 44종의 `id` 는 절대 재사용하지 않는다(`EXISTING_CATALOG_IDS`). 기존 테스트가 의존한다.
 */

import type { Cell3, CatalogId } from '../core/types'
import { CELL_SIZE_METERS } from '../core/types'
import type { CatalogItem } from './catalog'

/* ------------------------------------------------------------------------------------------------
 * 크기 등급
 * ---------------------------------------------------------------------------------------------- */

/**
 * 배치기가 "이 소품을 어디에 놓을 수 있는가"를 결정하는 등급.
 *
 * footprint(셀 크기)만으로는 이 판단이 안 된다 - 액자와 협탁은 부피가 비슷하지만 하나는 벽에,
 * 하나는 바닥에 놓인다. 크기와 부착면은 **직교하는 두 축**이라 별도 필드가 필요하다.
 *
 * `wall` / `ceiling` 을 반드시 채워야 하는 이유: 이 두 등급이 비면 복도처럼 바닥 면적만 있고
 * 가구가 안 들어가는 구획이 **영원히 텅 빈 채로** 남는다. 복도에도 볼 것이 있어야 한다.
 *
 * 타입이 아니라 배열이 원본인 이유는 `catalog.ts` 의 `ROOM_TAGS` 와 같다 -
 * 테스트가 등급을 런타임에 순회해야 하고, 유니온만 있으면 목록 사본이 생겨 반드시 뒤처진다.
 */
export const SIZE_CLASSES = ['anchor', 'floor', 'surface', 'wall', 'ceiling'] as const

/**
 *  - `anchor`  : 벽에 등을 붙이는 큰 가구(소파·침대·책장·냉장고). 시야 차단물이기도 하다.
 *  - `floor`   : 바닥 위 중간 크기(의자·테이블·화분·휴지통). 자유롭게 선다.
 *  - `surface` : 가구 상판 위 소품(머그·책·접시). 3절대로 부모-자식이 아니라 **y가 높은 셀**일 뿐이다.
 *  - `wall`    : 벽에 거는 것(거울·벽등·상부장).
 *  - `ceiling` : 천장에 매다는 것(실링팬·천장등).
 */
export type SizeClass = (typeof SIZE_CLASSES)[number]

/* ------------------------------------------------------------------------------------------------
 * 머티리얼 슬롯
 * ---------------------------------------------------------------------------------------------- */

/**
 * Kenney Furniture Kit 140종이 공유하는 **의미 기반** 머티리얼 슬롯 15종.
 * `models.json` 의 모든 `materials` 항목을 집계해 뽑은 실측 목록이며, 같은 이름은 키트 전체에서
 * 정확히 같은 색값을 갖는다(ASSETS.md 3.1). 사용 빈도 순으로 적었다.
 */
export const FURNITURE_MATERIAL_SLOTS = [
  'wood',
  'metal',
  'metalDark',
  'carpet',
  '_defaultMat',
  'metalMedium',
  'carpetWhite',
  'glass',
  'woodDark',
  'metalLight',
  'lamp',
  'carpetBlue',
  'carpetDarker',
  'plant',
  'fur',
] as const

/**
 * 변환된 Food Kit 56종의 슬롯. 원본이 텍스처 아틀라스라 의미명이 없어
 * 면적 지배 순으로 `slot0` ~ `slot6` 이 붙어 있다(ASSETS.md 4절).
 *
 * 매니페스트의 실제 문자열은 `slot0_945841` 처럼 **대표색 hex가 접미사로 붙는다.**
 * hex는 모델마다 다르므로 리터럴 유니온으로 적을 수 없고, 적어서도 안 된다 -
 * 카탈로그가 특정 색값에 묶이면 색 변경 동사의 의미가 사라진다.
 * 그래서 인덱스만 슬롯으로 쓰고, 매니페스트 대조는 `materialSlotMatches()` 가 담당한다.
 */
export const FOOD_MATERIAL_SLOTS = ['slot0', 'slot1', 'slot2', 'slot3', 'slot4', 'slot5', 'slot6'] as const

export type FurnitureMaterialSlot = (typeof FURNITURE_MATERIAL_SLOTS)[number]
export type FoodMaterialSlot = (typeof FOOD_MATERIAL_SLOTS)[number]

/** 카탈로그가 지목할 수 있는 머티리얼 슬롯 전체. */
export type MaterialSlot = FurnitureMaterialSlot | FoodMaterialSlot

/** `MaterialSlot` 판별용 런타임 집합. `assertEntryInvariants` 와 테스트가 쓴다. */
export const ALL_MATERIAL_SLOTS: ReadonlySet<string> = new Set<string>([
  ...FURNITURE_MATERIAL_SLOTS,
  ...FOOD_MATERIAL_SLOTS,
])

/** Food Kit 변환 슬롯명의 `slotN` 과 대표색 hex를 가르는 구분자. */
const FOOD_SLOT_SEPARATOR = '_'

/**
 * 카탈로그가 선언한 슬롯이 매니페스트의 머티리얼 이름과 같은 것을 가리키는지 판정한다.
 *
 * Furniture Kit은 이름이 그대로 일치하고, Food Kit은 `slot0` 이 `slot0_945841` 을 가리킨다.
 * 접두사 비교를 `startsWith('slot0')` 로 하면 `slot0` 이 존재하지 않는 모델에서
 * 오탐이 날 수 있으므로 **구분자 `_` 까지 포함해** 비교한다.
 */
export function materialSlotMatches(slot: MaterialSlot, materialName: string): boolean {
  return materialName === slot || materialName.startsWith(`${slot}${FOOD_SLOT_SEPARATOR}`)
}

/* ------------------------------------------------------------------------------------------------
 * 엔트리 초안
 * ---------------------------------------------------------------------------------------------- */

/** `weight` 의 유효 범위. 흔한 물건일수록 높다. */
export const MIN_ENTRY_WEIGHT = 1
export const MAX_ENTRY_WEIGHT = 10

/**
 * 각 그룹이 작성할 카탈로그 엔트리.
 *
 * `CatalogItem`(= `CatalogEntry` + `tags`)의 **확장**으로 정의한 것이 이 계약의 핵심이다.
 * 초안이 그대로 `CATALOG` 배열에 들어갈 수 있어야 병합 단계에서 변환 코드가 필요 없고,
 * 변환 코드가 없으면 변환 중에 값이 뒤바뀌는 사고도 없다.
 */
export type CatalogEntryDraft = CatalogItem &
  Readonly<{
    /**
     * `public/assets/models/<modelId>.glb` 의 파일명(확장자 제외).
     * 렌더가 실제로 불러오는 유일한 키이며, 두 엔트리가 같은 `modelId` 를 쓰면
     * 화면에 같은 물건이 두 이름으로 나타난다 - 실루엣 중복의 정의 그 자체라 금지한다.
     */
    modelId: string

    /** 배치기가 부착면을 고르는 등급. footprint로는 대신할 수 없다(위 주석 참조). */
    sizeClass: SizeClass

    /**
     * 색 변경 동사가 칠할 슬롯 **하나**.
     *
     * 전 슬롯을 한 색으로 칠하면 오브젝트가 단색 덩어리가 되어 실루엣 가독성이 떨어진다.
     * 소파는 천(`carpet`)만 바꾸고 다리(`wood`)는 그대로 두는 식으로 **의미 단위**를 지목한다.
     * 지목할 만한 슬롯이 없으면(전부 구조색이거나 면적이 너무 작으면) `null` - 색 변경 대상에서 빠진다.
     *
     * 선언한 슬롯은 그 모델의 `materials` 에 실제로 있어야 한다(`materialSlotMatches`).
     */
    recolorSlot: MaterialSlot | null

    /**
     * 배치 빈도 가중치(1~10, 정수). 흔한 물건일수록 높게.
     *
     * 균등 분포로 뽑으면 아파트에 변기가 아홉 개 놓인다. 밀도는 종 수가 아니라
     * **가중치 분포**에서 나온다 - 종을 늘리는 이번 작업이 실제로 효과를 보려면 이 값이 정직해야 한다.
     */
    weight: number
  }>

/* ------------------------------------------------------------------------------------------------
 * 실측 → 셀 변환
 * ---------------------------------------------------------------------------------------------- */

/**
 * 부동소수 나눗셈이 `2.0000000000000004` 같은 값을 만들어 `ceil` 이 한 칸을 더 얹는 것을 막는 여유값.
 * 셀 개수가 최대 15 수준이라 1e-9는 실제 경계(0.25m의 1e-9배 = 0.25nm)를 건드리지 않는다.
 */
const CEIL_EPSILON = 1e-9

/**
 * 매니페스트의 실측 크기(m)를 점유 셀 개수로 변환한다. **각 축 최소 1, 올림(ceil).**
 *
 * 손 추정을 금지하려고 존재하는 함수다. `models.json` 의 `sizeMeters` 배열은 `[x, y, z]` 순서이고
 * **y가 높이**다(`Cell3` 주석). 이 순서를 손으로 옮기다 y와 z를 바꾸는 것이 가장 흔한 사고이며,
 * 그때 생기는 결과는 "눕혀진 책장"이 아니라 **점유 셀이 어긋난 판정 오류**다.
 *
 * 올림인 이유: 실제 부피보다 점유 칸이 작으면 두 오브젝트가 화면에서 겹친다.
 * 최소 1인 이유: 셀보다 작은 소품을 허용하는 순간 "한 칸 이동"이 소품 하나보다 큰 어긋남이 되어
 * 4.1절의 그리드 해상도 근거가 무너진다(포크는 14cm지만 그래도 한 칸을 차지한다).
 */
export function cellsFromMeters(sizeMeters: Readonly<{ x: number; y: number; z: number }>): Cell3 {
  return {
    x: metersToCellCount(sizeMeters.x),
    y: metersToCellCount(sizeMeters.y),
    z: metersToCellCount(sizeMeters.z),
  }
}

function metersToCellCount(meters: number): number {
  if (!Number.isFinite(meters)) {
    throw new Error(`유한한 크기(m)가 아닙니다: ${String(meters)}`)
  }
  return Math.max(1, Math.ceil(meters / CELL_SIZE_METERS - CEIL_EPSILON))
}

/* ------------------------------------------------------------------------------------------------
 * 불변식 검사
 * ---------------------------------------------------------------------------------------------- */

/** `rotSymmetry` 로 허용되는 값. `RotSymmetry` 유니온의 런타임 짝이다. */
const VALID_ROT_SYMMETRIES: ReadonlySet<number> = new Set([1, 2, 4])

/** 기존 44종의 id 스냅샷. 새 엔트리가 이 중 하나를 쓰면 기존 테스트가 조용히 깨진다. */
export const EXISTING_CATALOG_IDS: readonly CatalogId[] = [
  'mug', 'book', 'trash-bin', 'potted-plant', 'chair', 'bench', 'table-dining', 'shelf-tall', 'sofa', 'rug-round',
  'cup', 'plate', 'bottle', 'computer-mouse', 'keyboard', 'doormat', 'pillow', 'laptop', 'microwave', 'lamp-table',
  'nightstand', 'monitor', 'cardboard-box', 'stool-bar', 'speaker', 'mirror', 'side-table', 'cabinet-tv',
  'television', 'desk', 'kitchen-cabinet', 'kitchen-stove', 'toilet', 'bathroom-sink', 'chair-desk', 'plant-large',
  'lamp-floor', 'bookcase-open', 'wardrobe', 'coat-rack', 'fridge', 'bed-double', 'bathtub', 'rug-rectangle',
]

/** `assertEntryInvariants` 의 선택 인자. 둘 다 생략하면 해당 검사만 건너뛴다. */
export type EntryInvariantOptions = Readonly<{
  /**
   * 존재하는 GLB 파일명 집합. 테스트가 `public/assets/models.json` 을 읽어 넘긴다.
   * 여기서 매니페스트를 직접 import하지 않는 이유: `models.json` 은 `public/` 자산이라
   * 번들에 들어가면 안 되고, 소스가 자산 사본을 들면 그 사본은 반드시 뒤처진다.
   */
  knownModelIds?: ReadonlySet<string>
  /** 이미 점유된 카탈로그 id. 보통 `EXISTING_CATALOG_IDS` 를 넘긴다. */
  reservedIds?: Iterable<CatalogId>
}>

/**
 * 엔트리 초안 묶음의 불변식을 전부 검사하고, 위반이 하나라도 있으면 **모두 모아** 던진다.
 *
 * 첫 위반에서 멈추지 않는 이유: 이 함수는 여러 명이 병렬로 쓴 파일을 받는 테스트에서 돌고,
 * 한 번에 하나씩 알려 주면 왕복이 위반 개수만큼 늘어난다.
 */
export function assertEntryInvariants(
  entries: readonly CatalogEntryDraft[],
  options: EntryInvariantOptions = {},
): void {
  const problems: string[] = []
  const seenIds = new Set<CatalogId>(options.reservedIds ?? [])
  const seenModelIds = new Map<string, CatalogId>()

  for (const entry of entries) {
    const where = `[${entry.id}]`

    if (seenIds.has(entry.id)) {
      problems.push(`${where} id가 중복이거나 기존 카탈로그와 충돌합니다`)
    }
    seenIds.add(entry.id)

    const previousOwner = seenModelIds.get(entry.modelId)
    if (previousOwner !== undefined) {
      problems.push(`${where} modelId '${entry.modelId}' 를 '${previousOwner}' 와 공유합니다 - 실루엣 중복입니다`)
    }
    seenModelIds.set(entry.modelId, entry.id)

    if (options.knownModelIds !== undefined && !options.knownModelIds.has(entry.modelId)) {
      problems.push(`${where} 존재하지 않는 modelId입니다: '${entry.modelId}'`)
    }

    for (const axis of ['x', 'y', 'z'] as const) {
      const size = entry.footprint[axis]
      if (!Number.isInteger(size) || size < 1) {
        problems.push(`${where} footprint.${axis} 는 1 이상의 정수여야 합니다 (현재 ${String(size)})`)
      }
    }

    if (!VALID_ROT_SYMMETRIES.has(entry.rotSymmetry)) {
      problems.push(`${where} rotSymmetry는 1·2·4 중 하나여야 합니다 (현재 ${String(entry.rotSymmetry)})`)
    }
    // 4.2절 규약: 완전 대칭 선언은 점유 칸이 정사각일 때만 성립한다.
    // 직사각 물체는 90° 돌리면 점유 모양 자체가 바뀌므로 "돌려도 무변화"일 수 없다.
    if (entry.rotSymmetry === 4 && entry.footprint.x !== entry.footprint.z) {
      problems.push(`${where} rotSymmetry 4는 footprint가 정사각(x === z)일 때만 허용됩니다`)
    }

    if (!SIZE_CLASSES.includes(entry.sizeClass)) {
      problems.push(`${where} 알 수 없는 sizeClass입니다: '${String(entry.sizeClass)}'`)
    }

    if (entry.recolorSlot !== null && !ALL_MATERIAL_SLOTS.has(entry.recolorSlot)) {
      problems.push(`${where} 알 수 없는 recolorSlot입니다: '${String(entry.recolorSlot)}'`)
    }

    if (
      !Number.isInteger(entry.weight) ||
      entry.weight < MIN_ENTRY_WEIGHT ||
      entry.weight > MAX_ENTRY_WEIGHT
    ) {
      problems.push(
        `${where} weight는 ${MIN_ENTRY_WEIGHT}~${MAX_ENTRY_WEIGHT} 정수여야 합니다 (현재 ${String(entry.weight)})`,
      )
    }

    if (entry.tags.length === 0) {
      problems.push(`${where} tags가 비어 있습니다 - 배치기가 이 소품을 어느 구획에도 놓지 못합니다`)
    }
  }

  if (problems.length > 0) {
    throw new Error(`카탈로그 엔트리 불변식 위반 ${problems.length}건:\n- ${problems.join('\n- ')}`)
  }
}
